import {
    createContext,
    ReactNode,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useSyncExternalStore,
} from "react";

type IGetSet<S> = {
    get: () => S;
    set: (updater: Partial<S> | ((prev: S) => Partial<S>)) => void;
};

type TCaseEvent = {
    [id: string]: (...props: any[]) => void
}

type IEvent<S, E extends TCaseEvent> = {
    [Name in keyof E]: (...props: ([...Parameters<E[Name]>, IGetSet<S> & {
        event: () => E
    }])) => void
}

export function createFastContext<S, CaseEvent extends TCaseEvent = {}>(initialState: S) {
    type StoreType = IGetSet<S> & {
        subscribe: (callback: () => void) => () => void;
        event?: IEvent<S, CaseEvent>;
    };
    const StoreContext = createContext<StoreType | null>(null);

    function Provider({ children, props = {}, event }: {
        children: ReactNode;
        props?: Partial<S>;
        event?: IEvent<S, CaseEvent>;
    }) {
        const store = useRef({ ...initialState, ...props });
        const subscribers = useRef(new Set<() => void>());
        const prevPropsRef = useRef<Partial<S>>(props);

        const get = useCallback(() => store.current, []);
        const set = useCallback((updater: Partial<S> | ((prev: S) => Partial<S>)) => {
            const nextState =
                typeof updater === "function" ? updater(store.current) : updater;
            store.current = { ...store.current, ...nextState };
            subscribers.current.forEach((cb) => cb());
        }, []);

        const subscribe = useCallback((cb: () => void) => {
            subscribers.current.add(cb);
            return () => subscribers.current.delete(cb);
        }, []);

        const contextValue = useMemo(
            () => ({ get, set, subscribe, event }),
            [get, set, subscribe, event]
        );

        useEffect(() => {
            const prev = prevPropsRef.current;

            const isChanged =
                Object.keys(props).some(
                    (key) => (props as any)[key] !== (prev as any)[key]
                ) ||
                Object.keys(prev).some(
                    (key) => (props as any)[key] !== (prev as any)[key]
                );

            if (isChanged) {
                store.current = { ...store.current, ...props };
                subscribers.current.forEach((cb) => cb());
                prevPropsRef.current = props;
            }
        }, [props]);

        return <StoreContext.Provider value={contextValue}>
            {children}
        </StoreContext.Provider>;
    }

    function useStore<Selected>(
        selector: (s: S) => Selected
    ): [Selected, IGetSet<S>["set"]] {
        const ctx = useContext(StoreContext);
        if (!ctx) throw new Error("useStore must be inside a Provider");

        const selected = useSyncExternalStore(
            ctx.subscribe,
            () => selector(ctx.get()),
            () => selector(initialState)
        );

        return [selected, ctx.set];
    }

    function GetStore(): () => S {
        const ctx = useContext(StoreContext);
        if (!ctx) throw new Error("getStore must be used inside Provider");
        return ctx.get;
    }

    function SetStore(): IGetSet<S>["set"] {
        const ctx = useContext(StoreContext);
        if (!ctx) throw new Error("getStore must be used inside Provider");
        return ctx.set;
    }

    function CallEvent(): CaseEvent {
        const ctx = useContext(StoreContext);
        if (!ctx) throw new Error("Store not found");

        const { event, get, set } = ctx;

        if (!event) {
            // return {} as any
            throw new Error("Event not created");
        }

        const createEvent = ({ get, set }: IGetSet<S>) => Object.keys(event).reduce((rs: any, key) => {
            rs[key] = (...payload: any) => {
                payload.push({ get, set, event: () => createEvent({ get, set }) })
                event[key](...payload);
            }
            return rs;
        }, {});

        return createEvent({ get, set });
    }

    return {
        Provider,
        useStore,
        getStore: GetStore,
        setStore: SetStore,
        callEvent: CallEvent
    };
}
